import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Link2,
  Copy,
  Check,
  Plus,
  Power,
  Trash2,
  ExternalLink,
  Loader2,
  ListChecks,
  Mail,
  Settings2,
  Columns3,
  List,
} from 'lucide-react';
import {
  VendorPageHeader,
  VendorGuideCallout,
  VendorEmptyState,
} from '../../components/vendor/guidance';
import {
  useVendorLeadLinks,
  useVendorExternalLeads,
  useVendorExternalLeadBoard,
  useVendorExternalLeadReminders,
  useCompleteExternalLeadReminder,
  useCreateLeadLink,
  useUpdateLeadLink,
  useDeleteLeadLink,
  useUpdateExternalLeadStatus,
} from '../../services/vendorExternalLeadService';
import { useToastContext } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { formatDate } from '../../utils/dateUtils';
import ChannelBadge from '../../components/shared/ChannelBadge';
import QuestionSchemaEditor from '../../components/shared/QuestionSchemaEditor';
import Input from '../../components/shared/Input';
import Button from '../../components/shared/Button';
import LeadPipelineBoard from '../../components/vendor/leads/LeadPipelineBoard';
import LeadFollowUpsPanel from '../../components/vendor/leads/LeadFollowUpsPanel';
import ExternalLeadDetailPanel from '../../components/vendor/leads/ExternalLeadDetailPanel';
import {
  DEFAULT_EXTERNAL_FORM_FIELDS,
  EXTERNAL_LEAD_STATUSES,
} from '../../utils/leadFormFields';

const VendorExternalLeads = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { success, error: showError } = useToastContext();
  const { confirm } = useConfirm();
  const view = searchParams.get('view') === 'list' ? 'list' : 'board';
  const selectedLeadId = searchParams.get('lead') || null;
  const reminderFilter = searchParams.get('reminders') || 'due_today';
  const [statusTab, setStatusTab] = useState('pending');
  const [copiedId, setCopiedId] = useState(null);
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [draftFields, setDraftFields] = useState([]);
  const [draftMeta, setDraftMeta] = useState({
    title: '',
    headline: '',
    welcome_message: '',
    thank_you_message: '',
  });

  const { data: linksRes, isLoading: linksLoading } = useVendorLeadLinks();
  const { data: boardRes, isLoading: boardLoading } = useVendorExternalLeadBoard(
    {},
    { enabled: view === 'board' }
  );
  const { data: leadsRes, isLoading: leadsLoading } = useVendorExternalLeads(
    { status: statusTab || undefined, per_page: 40 },
    { enabled: view === 'list' }
  );
  const { data: remindersRes, isLoading: remindersLoading } =
    useVendorExternalLeadReminders(reminderFilter);

  const createLink = useCreateLeadLink();
  const updateLink = useUpdateLeadLink();
  const deleteLink = useDeleteLeadLink();
  const updateStatus = useUpdateExternalLeadStatus();
  const completeReminder = useCompleteExternalLeadReminder();

  const links = linksRes?.data || [];
  const leads = leadsRes?.data?.data || leadsRes?.data || [];
  const counts = leadsRes?.counts || {};
  const columns = boardRes?.data?.columns || [];
  const reminders = remindersRes?.data || [];

  const setReminderFilter = (next) => {
    const p = new URLSearchParams(searchParams);
    if (!next || next === 'due_today') p.delete('reminders');
    else p.set('reminders', next);
    setSearchParams(p, { replace: true });
  };

  const origin = useMemo(
    () => (typeof window !== 'undefined' ? window.location.origin : ''),
    []
  );

  const setView = (next) => {
    const p = new URLSearchParams(searchParams);
    if (next === 'list') p.set('view', 'list');
    else p.delete('view');
    setSearchParams(p, { replace: true });
  };

  const openLead = (lead) => {
    const p = new URLSearchParams(searchParams);
    p.set('lead', lead.id);
    setSearchParams(p, { replace: true });
  };

  const closeLead = () => {
    const p = new URLSearchParams(searchParams);
    p.delete('lead');
    setSearchParams(p, { replace: true });
  };

  const shareUrlFor = (link) => {
    if (link.public_path) return `${origin}${link.public_path}`;
    const raw = link.public_url_slug || link.vendor?.business_name;
    const slug = raw
      ? String(raw)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
      : '';
    return `${origin}${slug ? `/lead/${slug}/${link.token}` : `/lead/${link.token}`}`;
  };

  const copyLink = async (link) => {
    const url = shareUrlFor(link);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(link.id);
      success(null, 'Link copied — paste it on social or send to a client.');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      showError(null, 'Could not copy link.');
    }
  };

  const handleCreateLink = async () => {
    try {
      const res = await createLink.mutateAsync({
        title: 'Booking inquiry',
        headline: 'Tell us about your event',
        form_fields: DEFAULT_EXTERNAL_FORM_FIELDS,
      });
      success(null, 'Lead link created — customize the form fields, then share.');
      if (res?.data) {
        openEditor(res.data);
        copyLink(res.data);
      }
    } catch (e) {
      showError(null, e?.response?.data?.message || 'Failed to create link.');
    }
  };

  const openEditor = (link) => {
    setEditingLinkId(link.id);
    setDraftFields(
      link.form_fields?.length ? link.form_fields : DEFAULT_EXTERNAL_FORM_FIELDS
    );
    setDraftMeta({
      title: link.title || '',
      headline: link.headline || '',
      welcome_message: link.welcome_message || '',
      thank_you_message: link.thank_you_message || '',
    });
  };

  const saveEditor = async () => {
    if (!editingLinkId) return;
    try {
      await updateLink.mutateAsync({
        id: editingLinkId,
        ...draftMeta,
        form_fields: draftFields.filter((f) => f.label?.trim()),
      });
      success(null, 'Form updated.');
      setEditingLinkId(null);
    } catch (e) {
      showError(null, e?.response?.data?.message || 'Failed to save form.');
    }
  };

  const toggleActive = async (link) => {
    try {
      await updateLink.mutateAsync({ id: link.id, is_active: !link.is_active });
      success(null, link.is_active ? 'Link disabled.' : 'Link enabled.');
    } catch (e) {
      showError(null, e?.response?.data?.message || 'Failed to update link.');
    }
  };

  const handleDeleteLink = async (link) => {
    const ok = await confirm({
      title: 'Delete lead link?',
      message: 'Existing leads stay in your inbox. This only removes the shareable form link.',
      confirmLabel: 'Delete link',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteLink.mutateAsync(link.id);
      success(null, 'Lead link deleted.');
      if (editingLinkId === link.id) setEditingLinkId(null);
    } catch (e) {
      showError(null, e?.response?.data?.message || 'Failed to delete link.');
    }
  };

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="External leads"
        subtitle="Same checklist pipeline as Internal — drag cards, complete stage tasks, then connect booking, invoice, and contract."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ChannelBadge channel="external" />
            <Button
              type="button"
              onClick={handleCreateLink}
              loading={createLink.isPending}
              disabled={createLink.isPending}
              icon={Plus}
              className="!h-8 !text-xs"
            >
              New share form
            </Button>
          </div>
        }
      />

      <VendorGuideCallout
        title="External pipeline"
        steps={[
          'Share a form link (Instagram, WhatsApp, website).',
          'Drag leads across Pending → Meeting scheduled → Meeting done (3-column board).',
          'Follow-ups appear after Meeting done — then convert and connect invoice / contract.',
        ]}
        icon={ListChecks}
      />

      <LeadFollowUpsPanel
        reminders={reminders}
        isLoading={remindersLoading}
        filter={reminderFilter}
        onFilterChange={setReminderFilter}
        onOpenLead={openLead}
        isCompleting={completeReminder.isPending}
        onComplete={async (reminderId, data) => {
          try {
            await completeReminder.mutateAsync({ reminderId, ...data });
            success(null, data.action === 'dismiss' ? 'Reminder dismissed.' : 'Follow-up logged.');
          } catch (e) {
            showError(null, e?.response?.data?.message || 'Could not update reminder.');
          }
        }}
        emptyHint="No reminders in this view. Follow-ups appear after an External lead is marked Meeting done."
      />

      <section className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm">
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-ink-tertiary" />
            <h2 className="text-sm font-semibold text-ink">Share forms</h2>
          </div>
        </div>
        <div className="p-4 sm:p-5">
          {linksLoading ? (
            <div className="py-10 text-center">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-ink" />
            </div>
          ) : links.length === 0 ? (
            <VendorEmptyState
              icon={Link2}
              title="No share forms yet"
              description="Create a form, pick the fields you want, then share the link with clients outside VendaList."
              actionLabel="Create share form"
              onAction={handleCreateLink}
            />
          ) : (
            <div className="space-y-3">
              {links.map((link) => {
                const url = shareUrlFor(link);
                const isEditing = editingLinkId === link.id;
                const fieldCount = (link.form_fields || DEFAULT_EXTERNAL_FORM_FIELDS).length;
                return (
                  <div key={link.id} className="product-surface-muted p-3 sm:p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-ink">{link.title || 'Lead link'}</p>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                              link.is_active
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {link.is_active ? 'Active' : 'Off'}
                          </span>
                          <span className="text-xs text-ink-tertiary">{fieldCount} fields</span>
                        </div>
                        <p className="mt-1 truncate text-xs text-ink-tertiary">{url}</p>
                        <p className="mt-1 text-xs text-ink-secondary">
                          {link.submissions_count || 0} submissions
                          {link.last_submitted_at
                            ? ` · last ${formatDate(link.last_submitted_at, 'short')}`
                            : ''}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => (isEditing ? setEditingLinkId(null) : openEditor(link))}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
                        >
                          <Settings2 className="h-3.5 w-3.5" />
                          {isEditing ? 'Close' : 'Edit form'}
                        </button>
                        <button
                          type="button"
                          onClick={() => copyLink(link)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
                        >
                          {copiedId === link.id ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                          Copy
                        </button>
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Open
                        </a>
                        <button
                          type="button"
                          onClick={() => toggleActive(link)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
                        >
                          <Power className="h-3.5 w-3.5" />
                          {link.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteLink(link)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {isEditing ? (
                      <div className="mt-4 space-y-4 rounded-md border border-border bg-surface p-3 sm:p-4">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Input
                            label="Form title"
                            value={draftMeta.title}
                            onChange={(e) =>
                              setDraftMeta((m) => ({ ...m, title: e.target.value }))
                            }
                            size="sm"
                          />
                          <Input
                            label="Headline"
                            value={draftMeta.headline}
                            onChange={(e) =>
                              setDraftMeta((m) => ({ ...m, headline: e.target.value }))
                            }
                            size="sm"
                          />
                          <Input
                            label="Welcome message"
                            value={draftMeta.welcome_message}
                            onChange={(e) =>
                              setDraftMeta((m) => ({ ...m, welcome_message: e.target.value }))
                            }
                            size="sm"
                          />
                          <Input
                            label="Thank-you message"
                            value={draftMeta.thank_you_message}
                            onChange={(e) =>
                              setDraftMeta((m) => ({ ...m, thank_you_message: e.target.value }))
                            }
                            size="sm"
                          />
                        </div>
                        <QuestionSchemaEditor
                          fields={draftFields}
                          onChange={setDraftFields}
                          title="What clients enter"
                          description="Same idea as Internal consultation questions — keep built-in event fields you need, remove the rest, or add custom questions."
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            onClick={saveEditor}
                            loading={updateLink.isPending}
                            disabled={updateLink.isPending}
                            className="!h-8 !text-xs"
                          >
                            Save form
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setEditingLinkId(null)}
                            className="!h-8 !text-xs"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-ink">Lead pipeline</h2>
          <div className="inline-flex rounded-lg border border-border bg-surface p-0.5">
            <button
              type="button"
              onClick={() => setView('board')}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
                view === 'board' ? 'bg-ink text-white' : 'text-ink-secondary hover:bg-surface-muted'
              }`}
            >
              <Columns3 className="h-3.5 w-3.5" />
              Board
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
                view === 'list' ? 'bg-ink text-white' : 'text-ink-secondary hover:bg-surface-muted'
              }`}
            >
              <List className="h-3.5 w-3.5" />
              List
            </button>
          </div>
        </div>

        {view === 'board' ? (
          boardLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-ink" />
            </div>
          ) : (
            <LeadPipelineBoard
              columns={columns}
              isMoving={updateStatus.isPending}
              onOpenLead={openLead}
              onMoveStatus={async (id, status) => {
                try {
                  await updateStatus.mutateAsync({ id, status });
                  success(null, 'Lead moved.');
                } catch (e) {
                  showError(
                    null,
                    e?.response?.data?.message || 'Could not move lead. Check allowed transitions.'
                  );
                }
              }}
            />
          )
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm">
            <div className="border-b border-border px-4 py-3 sm:px-5">
              <div className="-mx-1 overflow-x-auto overscroll-x-contain px-1 pb-0.5">
                <div className="product-tabs inline-flex min-w-max flex-nowrap" role="tablist">
                  {EXTERNAL_LEAD_STATUSES.map((tab) => {
                    const count = tab.key === '' ? counts.total : counts[tab.key];
                    const isActive = statusTab === tab.key;
                    return (
                      <button
                        key={tab.key || 'all'}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => setStatusTab(tab.key)}
                        className={`product-tab touch-manipulation whitespace-nowrap ${
                          isActive ? 'is-active' : ''
                        }`}
                      >
                        {tab.label}
                        {count != null ? (
                          <span className="ml-1 tabular-nums opacity-70">({count})</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="p-4 sm:p-5">
              {leadsLoading ? (
                <div className="py-12 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-ink" />
                </div>
              ) : leads.length === 0 ? (
                <VendorEmptyState
                  icon={Mail}
                  title="No leads in this view"
                  description="When someone submits your share form, they appear here with the same statuses as Internal leads."
                />
              ) : (
                <div className="space-y-2">
                  {leads.map((lead) => {
                    const progress = lead.checklist_progress || {};
                    return (
                      <button
                        key={lead.id}
                        type="button"
                        onClick={() => openLead(lead)}
                        className="flex w-full flex-col gap-2 rounded-lg border border-border bg-surface-muted/40 p-3 text-left transition hover:border-ink/30 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-ink">{lead.guest_name}</p>
                            <span className="rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] font-semibold uppercase text-ink-secondary">
                              {(lead.status_meta?.label || lead.status || '').replace(/_/g, ' ')}
                            </span>
                          </div>
                          <p className="mt-1 truncate text-xs text-ink-tertiary">
                            {lead.guest_email || lead.guest_phone || 'No contact'}
                            {lead.event_date
                              ? ` · ${formatDate(lead.event_date, 'short')}`
                              : ''}
                          </p>
                        </div>
                        <div className="text-xs text-ink-tertiary">
                          Checklist {progress.completed || 0}/{progress.total || 0}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {selectedLeadId ? (
        <ExternalLeadDetailPanel
          leadId={selectedLeadId}
          onClose={closeLead}
          onConverted={(bookingId) => {
            if (bookingId) navigate(`/vendor/bookings/${bookingId}`);
          }}
        />
      ) : null}

      <p className="text-center text-xs text-ink-tertiary">
        Prefer marketplace leads?{' '}
        <Link
          to="/vendor/consultations?view=board"
          className="font-medium text-ink underline-offset-2 hover:underline"
        >
          Open Internal leads
        </Link>
      </p>
    </div>
  );
};

export default VendorExternalLeads;
